import { AccountBalanceOutlined, CalendarMonthOutlined, Check, Close } from '@mui/icons-material';
import { Box, IconButton, Stack, Typography, useTheme } from '@mui/material';
import React, { useMemo } from 'react';
import { AttendanceStatus, Event } from '../../../types/Event';
import { UserConfigurableModule } from '../../../types/User';
import { humanizeEventDateTimes } from '../../../utility/dateUtils';
import { useUpdateEventAttendance } from '../../../services/react-query/events/useUpdateEventAttendance';
import DrawerLink from '../../common/drawers/ModuleDrawer/DrawerLink';
import {
  secondaryIconStyles,
  secondaryLabelStyles,
  secondaryTextStyles,
  textContainerStyles,
  tileContainerStyles,
  tileHeaderLeftStyles,
  tileHeaderSectionStyles,
  tileSectionLeftStyles,
  tileSectionRightStyles,
} from '../dashboardStyles';
import _ from 'lodash';

interface IEventTileProps {
  event: Event,
  attendance?: AttendanceStatus | null;
}

export function EventTile({ event, attendance }: IEventTileProps) {
  const theme = useTheme();
  const { mutate: updateAttendance } = useUpdateEventAttendance({ uuid: event.uuid });

  const attendanceStyles = useMemo(() => {
    if (!attendance) return {};

    const borderColor = {
      'pending': theme.palette.warning.main,
      'accepted': theme.palette.success.main,
      'declined': theme.palette.error.main,
    }[attendance];

    return { borderLeft: `5px solid ${borderColor}` };
  }, [attendance]);
  const showAccept = _.includes(['pending', 'declined'], attendance);
  const showDecline = _.includes(['pending', 'declined'], attendance);

  const dateLabel = humanizeEventDateTimes(event);
  return (
    <Box sx={[tileContainerStyles, attendanceStyles]}>
      <Box sx={tileSectionLeftStyles}>
        <Box sx={tileHeaderSectionStyles}>
          <DrawerLink module={UserConfigurableModule.EVENTS} uuid={event.uuid}>
            <Typography title={event.name} sx={tileHeaderLeftStyles}>
              {event.name}
            </Typography>
          </DrawerLink>
        </Box>

        <Box sx={textContainerStyles}>
          <Box sx={secondaryTextStyles}>
            <CalendarMonthOutlined sx={secondaryIconStyles} />
            <Typography
              title={dateLabel}
              sx={{
                ...secondaryLabelStyles,
                textDecoration: 'none',
              }}
            >
              {dateLabel}
            </Typography>
          </Box>

          <Box sx={secondaryTextStyles}>
            <AccountBalanceOutlined sx={secondaryIconStyles} />

            <DrawerLink module={UserConfigurableModule.ACCOUNTS} uuid={event.account_uuid}>
              <Typography title={event.account_display_name} sx={secondaryLabelStyles}>
                {event.account_display_name}
              </Typography>
            </DrawerLink>
          </Box>
        </Box>
      </Box>
      <Box sx={tileSectionRightStyles}>
        <Stack spacing={1} justifyContent="center">
          {showAccept && (
            <IconButton
              size="small"
              onClick={() => updateAttendance('accepted')}
              sx={{ color: theme.palette.success.main }}
              title="Accept"
            >
              <Check />
            </IconButton>
          )}
          {showDecline && (
            <IconButton
              size="small"
              onClick={() => updateAttendance('declined')}
              sx={{ color: theme.palette.error.main }}
              title="Decline"
            >
              <Close />
            </IconButton>
          )}
        </Stack>
      </Box>
    </Box >
  );
}
